import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react'
import { jwtDecode } from 'jwt-decode'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import AuthService from '../features/auth/services/AuthService'

export const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient()

  // Estados principales
  const [user, setUser] = useState(null)
  const [tokens, setTokens] = useState({
    accessToken: null,
    refreshToken: null,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [initialLoad, setInitialLoad] = useState(true)
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem('rememberMe') === 'true',
  )
  const [tokenVerified, setTokenVerified] = useState(false)

  // Refs de control
  const verificationInProgress = useRef(false)
  const retryCount = useRef(0)
  const isMounted = useRef(true)

  // Decide en qué almacenamiento guardamos (localStorage o sessionStorage)
  const getStorage = useCallback(
    () => (rememberMe ? localStorage : sessionStorage),
    [rememberMe],
  )

  // --- Mutations de React Query ---
  const loginMutation = useMutation({
    mutationFn: ({ email, password }) => AuthService.login(email, password),
    onSuccess: (data, variables) => {
      const { remember = false } = variables
      setRemember(remember)
      updateAuthState(data.accessToken, data.refreshToken)
      setTokenVerified(true)
      retryCount.current = 0
    },
    onError: (err) => {
      setError(err.message || 'Error de autenticación')
      throw err
    },
  })

  const logoutMutation = useMutation({
    mutationFn: () => AuthService.logout(tokens.accessToken),
    onSuccess: () => {
      queryClient.clear()
      updateAuthState(null, null)
      setTokenVerified(false)
    },
    onError: (err) => {
      console.error('Error al revocar token:', err)
      queryClient.clear()
      updateAuthState(null, null)
      setTokenVerified(false)
    },
  })

  const refreshTokenMutation = useMutation({
    mutationFn: (refreshToken) => AuthService.refreshToken(refreshToken),
    onSuccess: (data) => {
      updateAuthState(data.accessToken, data.refreshToken)
      setTokenVerified(true)
      retryCount.current = 0
    },
    onError: async () => {
      await handleLogout()
    },
  })

  // Decodifica el JWT y actualiza el estado 'user'
  const updateUserFromToken = useCallback((token) => {
    if (!token) {
      setUser(null)
      return
    }
    try {
      const decoded = jwtDecode(token)
      setUser({
        id: decoded.sub || decoded.userId,
        username: decoded.username || decoded.sub,
        email: decoded.email,
        role: decoded.roles?.[0]?.replace('ROLE_', '') || 'user',
        expiresAt: decoded.exp ? new Date(decoded.exp * 1000) : null,
        avatarUrl: decoded.avatarUrl || decoded.picture || null,
      })
    } catch (err) {
      console.error('Error decodificando token:', err)
      setUser(null)
    }
  }, [])

  // Actualiza los tokens en storage y en estado, o los elimina si no hay token
  const updateAuthState = useCallback(
    (accessToken, refreshToken = null) => {
      const storage = getStorage()

      if (accessToken) {
        storage.setItem('accessToken', accessToken)
        if (refreshToken) storage.setItem('refreshToken', refreshToken)

        setTokens({
          accessToken,
          refreshToken: refreshToken || tokens.refreshToken,
        })
        updateUserFromToken(accessToken)
      } else {
        storage.removeItem('accessToken')
        storage.removeItem('refreshToken')
        setTokens({ accessToken: null, refreshToken: null })
        setUser(null)
        setTokenVerified(false)
      }
    },
    [getStorage, tokens.refreshToken, updateUserFromToken],
  )

  // Maneja el logout completo (revocar en back y limpiar estado)
  const handleLogout = useCallback(async () => {
    try {
      if (tokens.accessToken) {
        await logoutMutation.mutateAsync()
      }
    } catch (err) {
      console.error('Error en logout:', err)
    } finally {
      queryClient.clear()
      updateAuthState(null, null)
      setTokenVerified(false)
    }
  }, [logoutMutation, tokens.accessToken, queryClient, updateAuthState])

  // Verifica con el servidor si el token sigue siendo válido
  const verifyTokenWithServer = useCallback(async (token) => {
    if (!token) return false
    try {
      const response = await AuthService.verifyToken(token)
      return response.isValid
    } catch (err) {
      console.warn('Error verificando token:', err)
      return false
    }
  }, [])

  // Carga inicial del usuario: lee tokens, verifica expiración y refresca si hace falta
  const loadUser = useCallback(async () => {
    if (
      !isMounted.current ||
      verificationInProgress.current ||
      retryCount.current > 2
    ) {
      setInitialLoad(false)
      setLoading(false)
      return
    }

    retryCount.current += 1
    setLoading(true)
    setError(null)

    try {
      const storage = getStorage()
      const accessToken = storage.getItem('accessToken')
      const refreshToken = storage.getItem('refreshToken')

      if (!accessToken) {
        await handleLogout()
        return
      }

      // Verificación local del token
      let isTokenValid = false
      try {
        const decoded = jwtDecode(accessToken)
        isTokenValid = !(decoded.exp && decoded.exp * 1000 < Date.now())
      } catch (err) {
        console.warn('Token inválido:', err)
        isTokenValid = false
      }

      // Si localmente es válido pero no hemos verificado con el servidor
      if (isTokenValid && !tokenVerified) {
        if (!verificationInProgress.current) {
          verificationInProgress.current = true
          try {
            const serverValid = await verifyTokenWithServer(accessToken)
            if (serverValid) {
              setTokenVerified(true)
              setTokens({ accessToken, refreshToken })
              updateUserFromToken(accessToken)
            } else {
              isTokenValid = false
            }
          } catch (err) {
            console.warn('Error en verificación con servidor:', err)
            isTokenValid = false
          } finally {
            verificationInProgress.current = false
          }
        }
      }

      // Si no es válido, tratar de refrescar o hacer logout
      if (!isTokenValid) {
        if (refreshToken) {
          try {
            await refreshTokenMutation.mutateAsync(refreshToken)
          } catch (refreshErr) {
            console.warn('Error refrescando token:', refreshErr)
            await handleLogout()
          }
        } else {
          await handleLogout()
        }
      }
    } catch (err) {
      console.error('Error cargando usuario:', err)
      await handleLogout()
    } finally {
      if (isMounted.current) {
        setLoading(false)
        setInitialLoad(false)
        retryCount.current = 0
      }
    }
  }, [
    getStorage,
    handleLogout,
    refreshTokenMutation,
    updateUserFromToken,
    tokenVerified,
    verifyTokenWithServer,
  ])

  // Cambia la preferencia de "recordarme" y traslada los tokens al storage adecuado
  const setRemember = useCallback((value) => {
    const fromStorage = value ? sessionStorage : localStorage
    const toStorage = value ? localStorage : sessionStorage
    const prevAccess = fromStorage.getItem('accessToken')
    const prevRefresh = fromStorage.getItem('refreshToken')

    setRememberMe(value)
    localStorage.setItem('rememberMe', value.toString())

    if (prevAccess) {
      toStorage.setItem('accessToken', prevAccess)
      if (prevRefresh) toStorage.setItem('refreshToken', prevRefresh)
      fromStorage.removeItem('accessToken')
      fromStorage.removeItem('refreshToken')
    }
  }, [])

  // Solo ejecutar carga inicial una vez al montar
  useEffect(() => {
    isMounted.current = true
    loadUser()
    return () => {
      isMounted.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Función para iniciar sesión desde componentes
  const handleLogin = async (email, password, remember = false) => {
    setLoading(true)
    try {
      await loginMutation.mutateAsync({ email, password, remember })
      return true
    } catch (err) {
      console.error('Error en login:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const combinedLoading =
    loading ||
    loginMutation.isPending ||
    logoutMutation.isPending ||
    refreshTokenMutation.isPending

  const contextValue = {
    user,
    tokens,
    loading: combinedLoading,
    error:
      error ||
      loginMutation.error?.message ||
      refreshTokenMutation.error?.message,
    rememberMe,
    initialLoad,
    isAuthenticated: !!user && tokenVerified,
    handleLogin,
    handleLogout,
    setRememberMe: setRemember,
    hasRole: (requiredRole) => {
      if (!user?.role) return false
      return Array.isArray(requiredRole)
        ? requiredRole.includes(user.role)
        : user.role === requiredRole
    },
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}
